import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-ui transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-shire-sun/20 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "rounded-xl border-2 border-shire-bark/50 bg-shire-sun font-semibold text-shire-dark shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-shire-bark hover:text-shire-light hover:shadow-lg",
        destructive:
          "rounded-xl border-2 border-red-600/50 bg-red-600 font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-lg",
        secondary:
          "rounded-xl border-2 border-shire-pine/50 bg-shire-pine font-semibold text-shire-light shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-shire-bark hover:text-shire-light hover:shadow-lg",
        outline:
          "rounded-xl border-2 border-shire-bark/10 bg-white/90 font-semibold text-shire-dark hover:border-shire-pine hover:bg-shire-stone/40",
        ghost:
          "rounded-xl text-shire-bark hover:bg-white/60 hover:text-shire-dark",
        link: "text-shire-pine underline-offset-4 hover:text-shire-bark hover:underline",
        quest:
          "rounded-xl border-2 border-green-600/50 bg-green-600 font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-700 hover:shadow-lg",
        locked: "cursor-not-allowed rounded-full bg-gray-300 text-gray-500",
        adventure:
          "rounded-xl border-2 border-shire-bark/50 bg-shire-bark font-semibold text-shire-light shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-shire-dark hover:shadow-lg",
        forge:
          "rounded-xl border-2 border-red-600/50 bg-red-600 font-semibold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-lg",
      },
      size: {
        sm: "h-8 rounded-lg px-3 text-xs",
        default: "h-10 px-6 py-3 text-base",
        md: "h-10 px-4 py-2 text-sm sm:h-12 sm:px-8 sm:py-4 sm:text-lg",
        lg: "h-12 px-8 py-4 text-lg",
        xl: "h-14 px-10 py-5 text-xl",
        icon: "size-10 rounded-full",
        "icon-sm": "size-8 rounded-full",
        "icon-lg": "size-12 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
